/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User } from 'firebase/auth';
import { auth } from '../services/FirestoreService';
import { Subscription, stripeService } from '../services/StripeService';
import './auth.css';

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '5px 20px 5px',
  borderRadius: '10px',
  width: '500px',
  maxHeight: '90%',
  overflowY: 'auto',
  textShadow: 'none',
  color: 'black',
  fontFamily: 'arial',
  textAlign: 'center',
};

const buttonStyle: React.CSSProperties = {
  fontSize: 'medium',
  padding: '10px 20px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  marginTop: '20px',
};

interface SubscriptionSuccessProps {
  onClose: () => void;
}

const SubscriptionSuccess: React.FC<SubscriptionSuccessProps> = ({onClose}) => {
  const [searchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (user) {
        // Get subscription details
        fetchSubscriptionDetails(user);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchSubscriptionDetails = async (user: User) => {
    try {
      const subscription = await stripeService.getCurrentSubscription(user);
      setSubscription(subscription);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
          <h2>Loading subscription details...</h2>
          <p>Please wait while we verify your subscription.</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
          <h2>Session Expired</h2>
          <p>Your session has expired. Please log in again to view your subscription details.</p>
          <button style={buttonStyle} onClick={onClose}>Return to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalStyle}>
        <h1 style={{ color: '#4CAF50' }}>Subscription Successful!</h1>
        
        <div style={{ margin: '20px 0', padding: '20px', backgroundColor: '#f8f8f8', borderRadius: '5px' }}>
          <h2>Thank You for Your Purchase</h2>
          <p>Your <strong>{subscription?.subscription}</strong> subscription is now active.</p>
          
          <div style={{ margin: '20px 0', textAlign: 'left', display: 'inline-block' }}>
            <h3>Subscription Details:</h3>
            <ul>
              <li>Subscription Type: <strong>{subscription?.subscription}</strong></li>
              <li>Monster Storage Capacity: <strong>{subscription?.monstersAllowed}</strong></li>
              <li>Billing: <strong>Annual</strong></li>
            </ul>
          </div>
          
          <p>You can now store up to <strong>{subscription?.monstersAllowed} monsters</strong> online!</p>
        </div>
        
        <p>You can manage your subscription from your account settings at any time.</p>
        
        <button style={buttonStyle} onClick={onClose}>Continue to Monster Forge</button>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;